import {
  SOCIAL_PIECE_STATUS_LABELS,
  type SocialPiece,
  type SocialPieceStatus,
} from '@growup/site-shared';
import { cx } from '@growup/ui';
import { Fragment } from 'react';

/** O fluxo das pecas, sem as abandonadas, que ficam a parte. */
export const FLOW: SocialPieceStatus[] = [
  'to_film',
  'to_edit',
  'to_approve',
  'to_publish',
  'published',
];

export type PlanPhase = 'not_started' | 'running' | 'closing' | 'done';

export const PLAN_PHASES: PlanPhase[] = ['not_started', 'running', 'closing', 'done'];

export const PLAN_PHASE_LABELS: Record<PlanPhase, string> = {
  not_started: 'Por começar',
  running: 'Em curso',
  closing: 'A fechar',
  done: 'Concluído',
};

const PHASE_HINTS: Record<PlanPhase, string> = {
  not_started: 'Ainda nada filmado.',
  running: 'Há peças a filmar, editar ou aprovar.',
  closing: 'Só falta publicar.',
  done: 'Tudo publicado ou abandonado.',
};

/** A fase do plano, calculada pelo estado das pecas. */
export function planPhase(pieces: readonly Pick<SocialPiece, 'status'>[]): PlanPhase {
  const counted = pieces.filter((p) => p.status !== 'abandoned');
  if (pieces.length > 0 && counted.every((p) => p.status === 'published')) return 'done';
  if (counted.every((p) => p.status === 'to_film')) return 'not_started';
  if (counted.every((p) => p.status === 'to_publish' || p.status === 'published')) {
    return 'closing';
  }
  return 'running';
}

export interface FlowState {
  status: SocialPieceStatus;
  count: number;
  /** Pecas com um passo de prazo passado. */
  overdue: number;
  /** Obrigatorias neste estado (fora "Publicada"). */
  must: number;
}

export function flowStates(pieces: readonly SocialPiece[]): FlowState[] {
  return [...FLOW, 'abandoned' as const].map((status) => {
    const here = pieces.filter((p) => p.status === status);
    const open = status !== 'published' && status !== 'abandoned';
    return {
      status,
      count: here.length,
      overdue: open ? here.filter((p) => p.steps.some((s) => s.overdue)).length : 0,
      must: open ? here.filter((p) => p.priority === 'must').length : 0,
    };
  });
}

/**
 * O estado por fazer com mais pecas paradas (o primeiro, se empatar). So com
 * o plano em curso ou a fechar: antes disso, estao todas por filmar.
 */
export function bottleneckOf(states: readonly FlowState[], phase: PlanPhase) {
  if (phase !== 'running' && phase !== 'closing') return null;
  let best: FlowState | null = null;
  for (const s of states) {
    if (s.status === 'published' || s.status === 'abandoned' || s.count === 0) continue;
    if (!best || s.count > best.count) best = s;
  }
  return best?.status ?? null;
}

/**
 * Topo do plano: a fase e a maquina de estados das pecas. Clicar num estado
 * filtra a Lista e o Quadro; clicar outra vez tira o filtro.
 */
export function PlanFlow({
  pieces,
  filter,
  onFilter,
}: {
  pieces: readonly SocialPiece[];
  filter: SocialPieceStatus | null;
  onFilter: (status: SocialPieceStatus | null) => void;
}) {
  const phase = planPhase(pieces);
  const states = flowStates(pieces);
  const bottleneck = bottleneckOf(states, phase);
  const abandoned = states.find((s) => s.status === 'abandoned');
  const toggle = (status: SocialPieceStatus) => onFilter(filter === status ? null : status);

  return (
    <div className="flex flex-col gap-3">
      <ol className="flex flex-wrap items-center gap-1 text-caption" aria-label="Fase do plano">
        {PLAN_PHASES.map((p, i) => (
          <Fragment key={p}>
            {i > 0 ? (
              <li aria-hidden className="text-gu-ink-muted">
                →
              </li>
            ) : null}
            <li
              aria-current={p === phase ? 'step' : undefined}
              className={cx(
                'border-2 px-2 py-0.5 font-sub font-bold uppercase',
                p === phase
                  ? 'border-gu-accent bg-gu-accent text-gu-on-accent'
                  : 'border-gu-line text-gu-ink-muted',
              )}
            >
              {PLAN_PHASE_LABELS[p]}
            </li>
          </Fragment>
        ))}
        <li className="ml-2 text-gu-ink-muted">{PHASE_HINTS[phase]}</li>
      </ol>

      <ol
        className="flex flex-col items-stretch gap-1 md:flex-row md:items-center"
        aria-label="Estados das peças"
      >
        {states
          .filter((s) => s.status !== 'abandoned')
          .map((s, i) => (
            <Fragment key={s.status}>
              {i > 0 ? (
                <li aria-hidden className="self-center text-gu-ink-muted">
                  <span className="md:hidden">↓</span>
                  <span className="hidden md:inline">→</span>
                </li>
              ) : null}
              <li className="md:flex-1">
                <StateBox
                  state={s}
                  active={filter === s.status}
                  bottleneck={bottleneck === s.status}
                  onClick={() => toggle(s.status)}
                />
              </li>
            </Fragment>
          ))}
      </ol>

      {abandoned && abandoned.count > 0 ? (
        <div>
          <StateBox
            state={abandoned}
            active={filter === 'abandoned'}
            bottleneck={false}
            onClick={() => toggle('abandoned')}
          />
        </div>
      ) : null}
    </div>
  );
}

function StateBox({
  state,
  active,
  bottleneck,
  onClick,
}: {
  state: FlowState;
  active: boolean;
  bottleneck: boolean;
  onClick: () => void;
}) {
  const label = SOCIAL_PIECE_STATUS_LABELS[state.status];
  const warnings = [
    state.overdue > 0 ? `${String(state.overdue)} com prazo passado` : null,
    state.must > 0
      ? `${String(state.must)} ${state.must === 1 ? 'obrigatória' : 'obrigatórias'}`
      : null,
  ].filter((w): w is string => w !== null);
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        'flex w-full flex-col gap-0.5 border-2 p-2 text-left',
        state.status === 'abandoned' ? 'md:w-auto' : '',
        active ? 'border-gu-ink bg-gu-raised' : 'border-gu-line bg-gu-sunken',
        bottleneck && !active ? 'border-gu-warning' : '',
        state.count === 0 ? 'text-gu-ink-muted' : '',
      )}
    >
      <span className="font-sub text-label font-bold uppercase">{label}</span>
      <span className="font-display text-title">{state.count}</span>
      {bottleneck ? <span className="text-caption text-gu-warning">Mais peças paradas</span> : null}
      {warnings.length > 0 ? (
        <span className="text-caption text-gu-danger">{warnings.join(' · ')}</span>
      ) : null}
    </button>
  );
}
